import * as React from 'react';
import { Alert, Form, Input, message, Radio, Select } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import OWButton from '../ow_button/OWButton';
import BackButton from '../back_button/BackButton';
import {
	TemplateDeliverableListFields,
	TemplateItemAddButton,
	TemplateItemRow,
	TemplateTaskListFields,
} from './ProjectTemplateItemFields';
// The options editor for a multiple-choice field. Imported from troubleshooting
// rather than reimplemented: it is a presentational Form.List over [{ value }] and
// this feature stores its choices in exactly that shape, so a second copy would only
// be a copy that drifts.
import TroubleshootingMultipleChoiceField from '../buyer_admin_settings_page/troubleshooting/TroubleshootingMultipleChoiceField';
import { v4 as uuidv4 } from 'uuid';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getDependencyCycleNames } from '../../utils/ProjectTemplateUtils';
import { isProjectsV2Enabled } from '../../utils/FeatureFlagUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	MIN_MULTIPLE_CHOICE_OPTIONS,
	PROJECT_TEMPLATE_FIELD_GROUPS,
	PROJECT_TEMPLATE_FIELD_TYPES,
	PROJECT_TEMPLATE_FIELD_TYPE_OPTIONS,
} from '../../constants/ProjectTemplateFields';
import {
	projectTemplatesRestCrudThunksForBuyer,
	projectTemplatesRestCrudThunksForSupplier,
} from '../../thunks/project_template_thunks';

const isMultipleChoice = (fieldDefinition) =>
	nullSafeGet('type', fieldDefinition) === PROJECT_TEMPLATE_FIELD_TYPES.multipleChoice;

// The type of one row of a field-definition list. Read by index off the group rather
// than through an interpolated nullSafeGet path — a field key can hold a period, and a
// path built from one silently mis-resolves.
const getFieldDefinitionType = (values, group, index) =>
	nullSafeGet('type', nullSafeGetOrElse(group, values, [])[index]);

// Trimmed, blank-free and de-duplicated. Two options that are both "HVAC" are one
// choice, not two, so counting them raw let a field clear the minimum while offering a
// single real answer — and the picker rendered duplicate React keys for them.
const getCleanChoices = (fieldDefinition) => {
	const seen = new Set();
	return nullSafeGetOrElse('multipleChoices', fieldDefinition, [])
		.map((choice) => ({ value: `${nullSafeGetOrElse('value', choice, '')}`.trim() }))
		.filter((choice) => {
			if (!choice.value || seen.has(choice.value)) {
				return false;
			}
			seen.add(choice.value);
			return true;
		});
};

// A row the user added and left blank is dropped rather than saved as an empty
// definition (the pre-antd-v4 form filtered the same way). These rows carry no
// required rule for that reason — validation would block the save before this could
// run, which is the same choice the deliverable and task lists make.
//
// A multiple-choice field additionally carries its options and whether more than one
// may be picked; every other type carries neither, so those keys are omitted rather
// than saved as empty on a text field.
const cleanFieldDefinitions = (fieldDefinitions) =>
	(fieldDefinitions || [])
		.filter((field) => nullSafeGet('key', field) && nullSafeGet('type', field))
		.map((field) =>
			isMultipleChoice(field)
				? {
						key: field.key,
						type: field.type,
						multipleChoices: getCleanChoices(field),
						allowMultipleAnswerSelections: !!field.allowMultipleAnswerSelections,
				  }
				: { key: field.key, type: field.type }
		);

// Named field definitions whose options don't clear the floor. Reported together so a
// template with several bad fields doesn't take several saves to fix.
const getUnderspecifiedChoiceFields = (fieldDefinitions) =>
	(fieldDefinitions || [])
		.filter((field) => nullSafeGet('key', field) && isMultipleChoice(field))
		.filter((field) => getCleanChoices(field).length < MIN_MULTIPLE_CHOICE_OPTIONS)
		.map((field) => field.key);

// A template task carries no due date, status or ordering id — array position IS the
// ordering, so reordering a list later is a plain splice. It does carry a time period
// and a default assignee (singular: a task has one assignee, a deliverable has many).
const cleanTaskTemplates = (tasks) =>
	(tasks || [])
		.filter((task) => nullSafeGet('title', task))
		.map((task) => ({
			title: task.title,
			daysForDue: cleanDaysForDue(task),
			assignTo: nullSafeGet('assignTo', task) || undefined,
		}));

// Blank stays blank: an item with no time period gets no pre-filled due date, which is
// different from one due on the project's start date (0).
function cleanDaysForDue(item) {
	const daysForDue = nullSafeGet('daysForDue', item);
	return daysForDue === undefined || daysForDue === null || daysForDue === ''
		? undefined
		: Number(daysForDue);
}

// Every surviving row carries a stable id, and a dependency is kept only when the row
// it names survived too — dropping the blank rows can remove the target of one, and a
// dependency pointing at a deliverable that no longer exists is worse than none.
const cleanDeliverableTemplates = (deliverables) => {
	const kept = (deliverables || [])
		.filter((deliverable) => nullSafeGet('name', deliverable))
		.map((deliverable) => ({
			...deliverable,
			id: nullSafeGet('id', deliverable) || uuidv4(),
		}));
	const keptIds = new Set(kept.map((deliverable) => deliverable.id));
	return kept.map((deliverable) => {
		const dependsOnId = nullSafeGet('dependsOnId', deliverable);
		return {
			id: deliverable.id,
			name: deliverable.name,
			description: deliverable.description,
			budget: deliverable.budget,
			daysForDue: cleanDaysForDue(deliverable),
			assignTo: nullSafeGetOrElse('assignTo', deliverable, []),
			dependsOnId: dependsOnId && keptIds.has(dependsOnId) ? dependsOnId : undefined,
			tasks: cleanTaskTemplates(deliverable.tasks),
		};
	});
};

// The template's custom field definitions ({ key, type }, plus options on a
// multiple-choice field) — one Form.List per required / optional group.
const FieldDefinitionListFields = ({ name, addLabel }) => (
	<Form.List name={name}>
		{(fields, { add, remove }) => (
			<>
				{fields.length > 0 ? (
					<div className="flex items-center gap-4">
						<div className="w-4" />
						<div className="footnoteregular text-character-secondary" style={{ width: 200 }}>
							Name
						</div>
						<div className="footnoteregular text-character-secondary" style={{ width: 160 }}>
							Type
						</div>
					</div>
				) : null}
				{fields.map(({ key, name: fieldName, ...restField }, index) => (
					<TemplateItemRow
						key={key}
						index={index}
						onRemove={() => remove(fieldName)}
						controls={
							<>
								<Form.Item
									{...restField}
									name={[fieldName, 'key']}
									className="mb-2"
									style={{ width: 200 }}
								>
									<Input />
								</Form.Item>
								<Form.Item {...restField} name={[fieldName, 'type']} className="mb-2">
									<Select style={{ width: 160 }} options={PROJECT_TEMPLATE_FIELD_TYPE_OPTIONS} />
								</Form.Item>
							</>
						}
					>
						{/* The options editor belongs to this row only, so it is gated on this
						    row's own type — hence a scoped shouldUpdate rather than a form-wide
						    watch, which would re-render every row on every keystroke. */}
						<Form.Item
							noStyle
							shouldUpdate={(prev, current) =>
								getFieldDefinitionType(prev, name, fieldName) !==
								getFieldDefinitionType(current, name, fieldName)
							}
						>
							{({ getFieldValue }) =>
								getFieldValue([name, fieldName, 'type']) ===
								PROJECT_TEMPLATE_FIELD_TYPES.multipleChoice ? (
									<div className="mb-4 border-0 border-l border-solid border-owgray pl-4">
										<Form.Item
											{...restField}
											name={[fieldName, 'allowMultipleAnswerSelections']}
											label="User can choose multiple answers"
											className="mb-2"
											rules={[{ required: true, message: 'Choose an option' }]}
										>
											<Radio.Group>
												<Radio value={true}>Yes</Radio>
												<Radio value={false}>No</Radio>
											</Radio.Group>
										</Form.Item>
										<TroubleshootingMultipleChoiceField name={[fieldName, 'multipleChoices']} />
									</div>
								) : null
							}
						</Form.Item>
					</TemplateItemRow>
				))}
				<TemplateItemAddButton
					label={addLabel}
					onClick={() => add({ type: PROJECT_TEMPLATE_FIELD_TYPES.string })}
				/>
			</>
		)}
	</Form.List>
);

const ProjectTemplateForm = ({
	formData,
	history,
	redirectForwardUrl,
	onSuccess,
	createErrors,
	updateErrors,
	creating,
	updating,
	currentUser,
	create,
	update,
}) => {
	const [form] = Form.useForm();
	const isUpdate = formData && formData.id !== undefined;
	const submitAction = isUpdate ? update : create;
	const submitText = isUpdate ? 'Update project template' : 'Create project template';
	const isLoading = updating || creating;
	// Deliverables / tasks on a template are part of projects-v2; unflagged tenants
	// see exactly the form they see today.
	const projectsV2 = isProjectsV2Enabled();
	const currencyId = nullSafeGetOrElse('id', getCurrency({ currentUser }), 'USD');

	const handleFinish = (values) => {
		// A one-option dropdown isn't a choice, and the options list carries no required
		// rule of its own (blank rows are dropped, not blocked) — so the floor is checked
		// here, the same place and the same way troubleshooting checks it.
		const underspecified = PROJECT_TEMPLATE_FIELD_GROUPS.reduce(
			(keys, group) => keys.concat(getUnderspecifiedChoiceFields(values[group])),
			[]
		);
		if (underspecified.length > 0) {
			message.error(
				`Multiple choice fields need at least ${MIN_MULTIPLE_CHOICE_OPTIONS} options: ${underspecified.join(
					', '
				)}.`
			);
			return;
		}

		// A dependency cycle is rejected by the deliverable API with a 400, which would
		// otherwise surface on a project whose author never wrote this template.
		const deliverableTemplates = projectsV2
			? cleanDeliverableTemplates(values.deliverableTemplates)
			: [];
		const cycleNames = getDependencyCycleNames(deliverableTemplates);
		if (cycleNames.length > 0) {
			message.error(`These deliverables depend on each other in a loop: ${cycleNames.join(', ')}.`);
			return;
		}

		const projectTemplate = {
			...formData,
			id: nullSafeGet('id', formData),
			name: values.name,
			buyerFacilityId: nullSafeGetOrElse(
				'buyerFacilityId',
				formData,
				nullSafeGet('facility.id', currentUser)
			),
			requiredFields: cleanFieldDefinitions(values.requiredFields),
			optionalFields: cleanFieldDefinitions(values.optionalFields),
			// When the flag is off the fields aren't rendered, so whatever a flagged
			// user already authored rides along untouched via the formData spread.
			...(projectsV2
				? {
						deliverableTemplates,
						taskTemplates: cleanTaskTemplates(values.taskTemplates),
				  }
				: {}),
		};

		submitAction(nullSafeGet('userType', currentUser), projectTemplate)
			.then((record) => {
				if (redirectForwardUrl) {
					history.push(redirectForwardUrl);
				}
				if (onSuccess) {
					onSuccess(record);
				}
			})
			// The failure is already surfaced by the create/update error Alert above the
			// form — swallow it here so it isn't also an unhandled rejection.
			.catch(() => {});
	};

	return (
		<Form
			form={form}
			layout="vertical"
			onFinish={handleFinish}
			className="supplierForm"
			initialValues={{
				name: nullSafeGet('name', formData),
				requiredFields: nullSafeGetOrElse('requiredFields', formData, []),
				optionalFields: nullSafeGetOrElse('optionalFields', formData, []),
				// A template saved before ids existed has none; mint them on load so the
				// dependency picker has something stable to reference from the first edit.
				deliverableTemplates: nullSafeGetOrElse('deliverableTemplates', formData, []).map(
					(deliverable) => ({
						...deliverable,
						id: nullSafeGet('id', deliverable) || uuidv4(),
					})
				),
				taskTemplates: nullSafeGetOrElse('taskTemplates', formData, []),
			}}
		>
			{createErrors.length > 0 ? (
				<Form.Item>
					<Alert message={createErrors.join(' ')} type="error" />
				</Form.Item>
			) : null}
			{updateErrors.length > 0 ? (
				<Form.Item>
					<Alert message={updateErrors.join(' ')} type="error" />
				</Form.Item>
			) : null}
			<Form.Item label="Name" name="name">
				<Input style={{ maxWidth: 300 }} />
			</Form.Item>
			<Form.Item label="Required Fields" className="mb-4">
				<FieldDefinitionListFields name="requiredFields" addLabel="Add required field" />
			</Form.Item>
			<Form.Item label="Optional Fields" className="mb-4">
				<FieldDefinitionListFields name="optionalFields" addLabel="Add optional field" />
			</Form.Item>
			{projectsV2 ? (
				<>
					<Form.Item
						label="Deliverables"
						className="mb-4"
						tooltip={{
							title:
								'Every project created from this template starts with these deliverables. Due dates come from each one’s time period and the project’s dates.',
							icon: <InfoCircleOutlined />,
						}}
					>
						<TemplateDeliverableListFields
							name="deliverableTemplates"
							currencyId={currencyId}
							form={form}
						/>
					</Form.Item>
					<Form.Item
						label="Tasks"
						className="mb-4"
						tooltip={{
							title: 'Tasks that belong to the project itself rather than to a deliverable.',
							icon: <InfoCircleOutlined />,
						}}
					>
						<TemplateTaskListFields name="taskTemplates" addLabel="Add task" />
					</Form.Item>
				</>
			) : null}
			<Form.Item>
				<OWButton
					type="primary"
					htmlType="submit"
					loading={isLoading}
					className="contactForm__button"
				>
					{submitText}
				</OWButton>
				<span style={{ marginLeft: '16px' }}>
					<BackButton buttonText="Cancel" />
				</span>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	formData: ownProps.formData,
	redirectForwardUrl: ownProps.redirectForwardUrl,
	onSuccess: ownProps.onSuccess,

	createErrors: state.project_templates.createErrors,
	updateErrors: state.project_templates.updateErrors,
	creating: state.project_templates.creating,
	updating: state.project_templates.updating,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
	update: (userType, entity) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectTemplatesRestCrudThunksForSupplier.update(entity)
				: projectTemplatesRestCrudThunksForBuyer.update(entity)
		),
	create: (userType, entity) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectTemplatesRestCrudThunksForSupplier.create(entity)
				: projectTemplatesRestCrudThunksForBuyer.create(entity)
		),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(ProjectTemplateForm)
);
